import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import {
  Search, Bell, User, ChevronDown, Command, Mic,
  Settings, LogOut, CreditCard, HelpCircle
} from 'lucide-react';

const Header = ({ title, subtitle }) => {
  const { isDark } = useTheme();
  const [searchFocused, setSearchFocused] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const notifications = [
    { id: 1, type: 'case', title: 'Case Update', desc: 'Hearing scheduled for tomorrow', time: '5m ago', unread: true },
    { id: 2, type: 'alert', title: 'Court Alert', desc: 'Order uploaded in WP/123/2024', time: '1h ago', unread: true },
    { id: 3, type: 'billing', title: 'Payment Received', desc: '₹25,000 from Client XYZ', time: '3h ago', unread: false },
  ];

  return (
    <header className="h-20 flex items-center justify-between px-8">
      {/* Left - Title */}
      <div>
        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-2xl font-bold"
        >
          {title}
        </motion.h1>
        {subtitle && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-sm text-gray-500"
          >
            {subtitle}
          </motion.p>
        )}
      </div>

      {/* Center - Search */}
      <motion.div
        className="flex-1 max-w-xl mx-8"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <motion.div
          className={`relative flex items-center rounded-2xl transition-all ${
            searchFocused ? 'shadow-lg shadow-blue-500/20' : ''
          }`}
          animate={{
            boxShadow: searchFocused
              ? '0 0 30px rgba(45, 140, 255, 0.3)'
              : '0 4px 20px rgba(0, 0, 0, 0.08)'
          }}
        >
          <div
            className="flex items-center w-full px-5 py-3 rounded-2xl"
            style={{
              background: isDark ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${searchFocused ? 'rgba(45, 140, 255, 0.5)' : isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
            }}
          >
            <Search className={`w-5 h-5 ${searchFocused ? 'text-blue-500' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Search cases, documents, clients..."
              className="flex-1 ml-3 bg-transparent outline-none text-sm"
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
            />
            <div className="flex items-center gap-2">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 rounded-lg hover:bg-gray-500/10"
              >
                <Mic className="w-4 h-4 text-gray-400" />
              </motion.button>
              <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-500/10">
                <Command className="w-3 h-3" />
                <span className="text-xs">K</span>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Right - Actions */}
      <div className="flex items-center gap-4">
        {/* Notifications */}
        <div className="relative">
          <motion.button
            onClick={() => { setNotifOpen(!notifOpen); setProfileOpen(false); }}
            className="relative p-3 rounded-xl glass-card"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Bell className="w-5 h-5" />
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center"
            >
              3
            </motion.span>
          </motion.button>

          <AnimatePresence>
            {notifOpen && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                className="absolute right-0 top-14 w-80 glass-card p-4 z-50"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">Notifications</h3>
                  <button className="text-xs text-blue-500">Mark all read</button>
                </div>
                <div className="space-y-3">
                  {notifications.map((notif) => (
                    <motion.div
                      key={notif.id}
                      className={`p-3 rounded-xl cursor-pointer transition-all ${
                        notif.unread ? 'bg-blue-500/10' : 'hover:bg-gray-500/10'
                      }`}
                      whileHover={{ x: 4 }}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-2 h-2 rounded-full mt-2 ${
                          notif.unread ? 'bg-blue-500' : 'bg-transparent'
                        }`} />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{notif.title}</p>
                          <p className="text-xs text-gray-500">{notif.desc}</p>
                          <p className="text-xs text-gray-400 mt-1">{notif.time}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
                <button className="w-full mt-4 p-2 text-center text-sm text-blue-500 hover:bg-blue-500/10 rounded-lg">
                  View All Notifications
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Profile */}
        <div className="relative">
          <motion.button
            onClick={() => { setProfileOpen(!profileOpen); setNotifOpen(false); }}
            className="flex items-center gap-3 p-2 pr-4 rounded-2xl glass-card"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="w-10 h-10 rounded-xl overflow-hidden animated-gradient flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium">Adv. VSKR</p>
              <p className="text-xs text-gray-500">Premium Plan</p>
            </div>
            <motion.div animate={{ rotate: profileOpen ? 180 : 0 }}>
              <ChevronDown className="w-4 h-4" />
            </motion.div>
          </motion.button>

          <AnimatePresence>
            {profileOpen && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                className="absolute right-0 top-16 w-56 glass-card p-2 z-50"
              >
                {[
                  { icon: User, label: 'My Profile', path: '/profile' },
                  { icon: Settings, label: 'Settings', path: '/settings' },
                  { icon: CreditCard, label: 'Billing', path: '/billing' },
                  { icon: HelpCircle, label: 'Help & Support', path: '/help' },
                ].map((item) => (
                  <motion.button
                    key={item.label}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-500/10"
                    whileHover={{ x: 4 }}
                  >
                    <item.icon className="w-4 h-4" />
                    <span className="text-sm">{item.label}</span>
                  </motion.button>
                ))}
                <hr className="my-2 border-gray-500/20" />
                <motion.button
                  className="w-full flex items-center gap-3 p-3 rounded-xl text-red-500 hover:bg-red-500/10"
                  whileHover={{ x: 4 }}
                >
                  <LogOut className="w-4 h-4" />
                  <span className="text-sm">Sign Out</span>
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
};

export default Header;
